/* global globalThis */
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Box, Paper, Typography, Divider, alpha } from '@mui/material';
import {
  BarChart,
  Bar,
  Cell,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { useTranslation } from 'react-i18next';

function getPerformanceLabel(score) {
  if (score >= 70) return 'Strong Performance';
  if (score >= 60) return 'Good Performance';
  if (score >= 50) return 'Neutral';
  return 'Needs Attention';
}

function PillarBarTooltip({ active, payload }) {
  const { t } = useTranslation();
  if (active && payload?.length) {
    const data = payload[0].payload;
    return (
      <Box sx={{
        bgcolor: 'white',
        p: 2,
        border: '1px solid #e0e0e0',
        borderRadius: 2,
        boxShadow: 2,
        minWidth: 250,
      }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: data.color }}>
          Pillar {data.pillar}: {data.pillarNameShort}
        </Typography>
        <Divider sx={{ my: 1 }} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="caption" color="text.secondary">{t('dashboard.pillarScores.normalizedScore')}:</Typography>
          <Typography variant="caption" sx={{ fontWeight: 600 }}>{data.score.toFixed(1)}</Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="caption" color="text.secondary">{t('dashboard.pillarScores.weight')}:</Typography>
          <Typography variant="caption" sx={{ fontWeight: 600 }}>{data.weight.toFixed(0)}%</Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="caption" color="text.secondary">{t('dashboard.pillarScores.weightedScore')}:</Typography>
          <Typography variant="caption" sx={{ fontWeight: 600 }}>{data.weighted.toFixed(2)}</Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="caption" color="text.secondary">{t('dashboard.pillarScores.indicators')}:</Typography>
          <Typography variant="caption" sx={{ fontWeight: 600 }}>{data.indicatorsCount}</Typography>
        </Box>
        <Typography variant="caption" sx={{ display: 'block', mt: 1, fontStyle: 'italic', color: 'primary.main' }}>
          {t('dashboard.pillarScores.clickToView')} →
        </Typography>
      </Box>
    );
  }
  return null;
}

PillarBarTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.arrayOf(PropTypes.shape({ payload: PropTypes.object })),
};

PillarBarTooltip.defaultProps = {
  active: false,
  payload: [],
};

function PillarRadarTooltip({ active, payload, getScoreColor }) {
  if (active && payload?.length) {
    const data = payload[0].payload;
    const scoreColor = getScoreColor(data.fullValue);
    return (
      <Box sx={{
        bgcolor: 'white',
        p: 2,
        border: '2px solid',
        borderColor: scoreColor,
        borderRadius: 2,
        boxShadow: 3,
        minWidth: 200,
      }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: scoreColor }}>
          Pillar {data.subject}
        </Typography>
        <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
          {data.pillarName}
        </Typography>
        <Divider sx={{ my: 1 }} />
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="caption" color="text.secondary">Score:</Typography>
          <Typography variant="h5" sx={{ fontWeight: 700, color: scoreColor }}>
            {data.fullValue.toFixed(1)}
          </Typography>
        </Box>
        <Box sx={{
          mt: 1,
          p: 1,
          borderRadius: 1,
          bgcolor: alpha(scoreColor, 0.1),
          textAlign: 'center',
        }}>
          <Typography variant="caption" sx={{ fontWeight: 600, color: scoreColor }}>
            {getPerformanceLabel(data.fullValue)}
          </Typography>
        </Box>
      </Box>
    );
  }
  return null;
}

PillarRadarTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.arrayOf(PropTypes.shape({ payload: PropTypes.object })),
  getScoreColor: PropTypes.func.isRequired,
};

PillarRadarTooltip.defaultProps = {
  active: false,
  payload: [],
};

const ChartsSection = ({ chartData, radarData, statistics, getScoreColor }) => {
  const { t } = useTranslation();
  const radarTooltip = useMemo(
    () => <PillarRadarTooltip getScoreColor={getScoreColor} />,
    [getScoreColor],
  );

  return (
    <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
      <Box sx={{ flex: 1 }}>
        <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2, height: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {t('dashboard.pillarScores.title')}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t('dashboard.pillarScores.subtitle')}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 12, height: 12, bgcolor: '#11998e', borderRadius: 1 }} />
                <Typography variant="caption">{t('dashboard.pillarScores.strong')}</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 12, height: 12, bgcolor: '#667eea', borderRadius: 1 }} />
                <Typography variant="caption">{t('dashboard.pillarScores.good')}</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 12, height: 12, bgcolor: '#ffa726', borderRadius: 1 }} />
                <Typography variant="caption">{t('dashboard.pillarScores.neutral')}</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 12, height: 12, bgcolor: '#ff6b6b', borderRadius: 1 }} />
                <Typography variant="caption">{t('dashboard.pillarScores.weak')}</Typography>
              </Box>
            </Box>
          </Box>
          <ResponsiveContainer width="100%" height={350} minWidth={400} minHeight={350}>
            <BarChart
              data={chartData}
              onClick={(data) => {
                if (data?.activeLabel) {
                  globalThis.location.assign(`/score?pillar=${data.activeLabel}`);
                }
              }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                dataKey="pillar"
                tick={{ fontSize: 12, fontWeight: 600 }}
              />
              <YAxis
                domain={[0, 100]}
                tick={{ fontSize: 12 }}
                label={{ value: 'Score', angle: -90, position: 'insideLeft', style: { fontSize: 12, fontWeight: 600 } }}
              />
              <Tooltip
                wrapperStyle={{ zIndex: 1000 }}
                content={<PillarBarTooltip />}
              />
              <Legend
                wrapperStyle={{ paddingTop: 20 }}
                iconType="circle"
              />
              <Bar
                dataKey="score"
                name={t('dashboard.pillarScores.normalizedScore')}
                radius={[8, 8, 0, 0]}
                cursor="pointer"
              >
                {chartData.map((entry) => (
                  <Cell key={`cell-${entry.pillar}`} fill={entry.color} />
                ))}
              </Bar>
              <Bar
                dataKey="weighted"
                fill="#764ba2"
                name={t('dashboard.pillarScores.weightedScore')}
                radius={[8, 8, 0, 0]}
                cursor="pointer"
              />
            </BarChart>
          </ResponsiveContainer>
        </Paper>
      </Box>

      <Box sx={{ flex: 1 }}>
        <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2, height: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {t('dashboard.radarChart.title')}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t('dashboard.radarChart.subtitle')}
              </Typography>
            </Box>
            <Box sx={{
              px: 2,
              py: 1,
              borderRadius: 2,
              bgcolor: alpha('#667eea', 0.1),
              border: '1px solid',
              borderColor: alpha('#667eea', 0.2),
            }}>
              <Typography variant="caption" color="text.secondary">{t('dashboard.radarChart.avgScore')}</Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#667eea' }}>
                {statistics.avgScore.toFixed(1)}
              </Typography>
            </Box>
          </Box>
          <ResponsiveContainer width="100%" height={350} minWidth={400} minHeight={350}>
            <RadarChart data={radarData}>
              <PolarGrid
                stroke="#e0e0e0"
                strokeWidth={1}
              />
              <PolarAngleAxis
                dataKey="subject"
                tick={{ fontSize: 14, fontWeight: 600, fill: '#667eea' }}
              />
              <PolarRadiusAxis
                angle={90}
                domain={[0, 100]}
                tick={{ fontSize: 11 }}
                tickCount={6}
              />
              <Radar
                name="Pillar Score"
                dataKey="value"
                stroke="#667eea"
                fill="#667eea"
                fillOpacity={0.6}
                strokeWidth={2}
                dot={{ r: 5, fill: '#667eea', strokeWidth: 2, stroke: '#fff' }}
                activeDot={{ r: 7, fill: '#764ba2', strokeWidth: 2, stroke: '#fff' }}
              />
              <Tooltip
                wrapperStyle={{ zIndex: 1000 }}
                content={radarTooltip}
              />
              <Legend
                wrapperStyle={{ paddingTop: 20 }}
                iconType="circle"
              />
            </RadarChart>
          </ResponsiveContainer>
        </Paper>
      </Box>
    </Box>
  );
};

ChartsSection.propTypes = {
  chartData: PropTypes.arrayOf(PropTypes.shape({
    pillar: PropTypes.string,
    color: PropTypes.string,
    score: PropTypes.number,
    weighted: PropTypes.number,
    weight: PropTypes.number,
    pillarNameShort: PropTypes.string,
    indicatorsCount: PropTypes.number,
  })).isRequired,
  radarData: PropTypes.arrayOf(PropTypes.shape({
    subject: PropTypes.string,
    value: PropTypes.number,
    fullValue: PropTypes.number,
    pillarName: PropTypes.string,
  })).isRequired,
  statistics: PropTypes.shape({
    avgScore: PropTypes.number,
  }).isRequired,
  getScoreColor: PropTypes.func.isRequired,
};

export default ChartsSection;

